import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutGrid } from "lucide-react";
import { Icons } from "../ui/icons";
import { cn } from "../../lib/utils";
import { NavItemConfig, isNavItemActive } from "../../lib/navigation";

export const MOBILE_TAB_ITEMS: NavItemConfig[] = [
  { href: "/", label: "Home", icon: Icons.Dashboard },
  {
    href: "/activities",
    label: "Movements",
    icon: Icons.Movement,
    isActive: (pathname) => pathname === "/activities" || pathname === "/add-activity",
  },
  { href: "/inventory", label: "Inventory", icon: Icons.Inventory },
  {
    href: "/sites",
    label: "Sites",
    icon: Icons.Building,
    isActive: (pathname) => pathname === "/sites" || pathname === "/site-details",
  },
];

interface MobileBottomNavProps {
  isMenuOpen: boolean;
  onOpenMenu: () => void;
}

const TabContent: React.FC<{
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}> = ({ icon, label, isActive }) => (
  <>
    <span className="relative flex h-8 w-14 items-center justify-center">
      {isActive && (
        <motion.span
          layoutId="mobile-tab-indicator"
          className="absolute inset-0 rounded-full bg-primary-50"
          transition={{ type: "spring", stiffness: 500, damping: 35 }}
        />
      )}
      <span className="relative">{icon}</span>
    </span>
    <span
      className={cn(
        "text-[11px] leading-none",
        isActive ? "font-semibold text-primary-700" : "font-medium text-gray-500",
      )}
    >
      {label}
    </span>
  </>
);

const tabClassName =
  "flex flex-1 flex-col items-center justify-center gap-1 pt-2 pb-1.5 select-none transition-transform active:scale-95 [-webkit-tap-highlight-color:transparent]";

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ isMenuOpen, onOpenMenu }) => {
  const { pathname } = useLocation();
  const activeTab = isMenuOpen
    ? undefined
    : MOBILE_TAB_ITEMS.find((item) => isNavItemActive(item, pathname));
  const isMoreActive = isMenuOpen || !activeTab;

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-100 bg-white/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_rgba(13,14,19,0.04)]"
    >
      <div className="flex h-16 items-stretch">
        {MOBILE_TAB_ITEMS.map((item) => {
          const isActive = activeTab?.href === item.href;
          return (
            <Link
              key={item.href}
              to={item.href}
              className={tabClassName}
              aria-current={isActive ? "page" : undefined}
            >
              <TabContent
                isActive={isActive}
                label={item.label}
                icon={
                  <item.icon
                    width={22}
                    height={22}
                    className={isActive ? "text-primary-600" : "text-gray-500"}
                  />
                }
              />
            </Link>
          );
        })}
        <button
          type="button"
          onClick={onOpenMenu}
          className={cn(tabClassName, "bg-transparent border-0 rounded-none p-0")}
          aria-haspopup="dialog"
          aria-expanded={isMenuOpen}
        >
          <TabContent
            isActive={isMoreActive}
            label="More"
            icon={
              <LayoutGrid
                size={21}
                strokeWidth={1.75}
                className={isMoreActive ? "text-primary-600" : "text-gray-500"}
              />
            }
          />
        </button>
      </div>
    </nav>
  );
};

export default MobileBottomNav;
